/**
 * `/hr/performance` — the Performance screen, WP-W8.
 * `web/shapes/hr-performance.md` is the agreed shape, written before this file.
 *
 * The one HR surface every employee reaches, not only an administrator:
 * `src/lib/hiring-permissions.ts`'s `SELF_SERVICE_FLOOR` gives every role
 * `hr.view_own_review` before any grant row, so `nav/menu.ts` declares this item with
 * no role check at all. Three sections:
 *
 *  - **My review** (`MyReviewPanel`) — always rendered. Three independent regions
 *    (`PRODUCT.md` §4): the caller's own reviews, their own goals, and a self-review
 *    form. `hr.view_own_review` is on the floor but `hr.submit_review` is not, so an
 *    employee who has never been granted `submit_review` still reads both lists while
 *    the self-review submit shows its own `403` in place (`web/shapes/hr-performance.md`
 *    §3).
 *  - **Manage** (`PerformanceManagePanel`) — rendered only when the session holds
 *    `hr.manage_review_cycles`, the same client-side honesty gate `LeaveScreen`'s
 *    approvals section already uses. Review cycles (list + create) and, for a typed
 *    employee id, that employee's goals (list + create).
 *  - **Review** (`TeamReviewsPanel`) — rendered only when the session holds
 *    `hr.view_reviews`. For a typed employee id: their reviews, and a manager-review
 *    submit form with no reviewer field of any kind.
 *
 * The self-review and manager-review submit bodies are built entirely by the two panels
 * from `ReviewSubmitRequest` — neither ever adds a reviewer id, because the type this
 * screen mirrors from `openapi.yaml` has no member for one (`api/hr.ts`'s own header).
 */

import { useState, type ReactNode } from 'react';

import { useI18n } from '../../i18n';
import { useSession } from '../../auth/SessionProvider';
import type {
  Goal,
  GoalCreateRequest,
  GoalListResponse,
  Review,
  ReviewCycle,
  ReviewCycleCreateRequest,
  ReviewCycleListResponse,
  ReviewListResponse,
  ReviewSubmitRequest,
} from '../../api/hr';
import {
  HR_PERFORMANCE_GOALS_PATH,
  HR_PERFORMANCE_REVIEW_CYCLES_PATH,
  HR_PERFORMANCE_REVIEWS_MINE_PATH,
  HR_PERFORMANCE_REVIEWS_PATH,
  hrPerformanceGoalsPath,
  hrPerformanceReviewsPath,
} from '../../api/hr';
import { useApiMutation } from '../../api/useApiMutation';
import { useApiQuery } from '../../api/useApiQuery';
import { ScreenHeading } from '../../shell/ScreenHeading';
import { MyReviewPanel } from './MyReviewPanel';
import { PerformanceManagePanel } from './PerformanceManagePanel';
import { TeamReviewsPanel } from './TeamReviewsPanel';

export function PerformanceScreen(): ReactNode {
  const { t } = useI18n();
  const { user } = useSession();
  const selfEmployeeId = user?.employee_id ?? null;
  const mayManage = (user?.permissions ?? []).includes('hr.manage_review_cycles');
  const mayReview = (user?.permissions ?? []).includes('hr.view_reviews');

  /* ── My review ──────────────────────────────────────────────────────────── */

  const [myReviewsToken, setMyReviewsToken] = useState(0);
  const myReviews = useApiQuery<ReviewListResponse>(
    HR_PERFORMANCE_REVIEWS_MINE_PATH,
    myReviewsToken,
  );

  // Nothing on this screen ever writes a goal on the caller's own behalf (goals are
  // assigned, never self-created — `web/shapes/hr-performance.md` §5), so there is no
  // reload token to bump here: `myGoals.reload`, wired to the region's own retry
  // button, is the only re-read this needs — the same reasoning `LeaveScreen` gives
  // for its own balances read.
  const myGoalsPath =
    selfEmployeeId === null ? null : hrPerformanceGoalsPath({ employeeId: selfEmployeeId });
  const myGoals = useApiQuery<GoalListResponse>(myGoalsPath);

  const submitSelfReview = useApiMutation<ReviewSubmitRequest, Review>(HR_PERFORMANCE_REVIEWS_PATH);

  const onSubmitSelfReview = (body: ReviewSubmitRequest): void => {
    submitSelfReview
      .submit(body)
      .then(() => {
        setMyReviewsToken((value) => value + 1);
      })
      .catch(() => {
        /* `submit` resolves rather than rejects. */
      });
  };

  /* ── Manage (hr.manage_review_cycles) ──────────────────────────────────── */

  const [cyclesToken, setCyclesToken] = useState(0);
  const cycles = useApiQuery<ReviewCycleListResponse>(
    HR_PERFORMANCE_REVIEW_CYCLES_PATH,
    cyclesToken,
  );
  const createCycle = useApiMutation<ReviewCycleCreateRequest, ReviewCycle>(
    HR_PERFORMANCE_REVIEW_CYCLES_PATH,
  );

  const onCreateCycle = (body: ReviewCycleCreateRequest): void => {
    createCycle
      .submit(body)
      .then(() => {
        setCyclesToken((value) => value + 1);
      })
      .catch(() => {
        /* resolves rather than rejects */
      });
  };

  const [manageGoalsEmployeeId, setManageGoalsEmployeeId] = useState<string | null>(null);
  const [manageGoalsToken, setManageGoalsToken] = useState(0);
  const manageGoalsPath =
    manageGoalsEmployeeId === null
      ? null
      : hrPerformanceGoalsPath({ employeeId: manageGoalsEmployeeId });
  const manageGoals = useApiQuery<GoalListResponse>(manageGoalsPath, manageGoalsToken);
  const createGoal = useApiMutation<GoalCreateRequest, Goal>(HR_PERFORMANCE_GOALS_PATH);

  const loadManageGoalsEmployee = (id: string): void => {
    createGoal.reset();
    setManageGoalsEmployeeId(id);
  };

  const onCreateGoal = (body: GoalCreateRequest): void => {
    createGoal
      .submit(body)
      .then(() => {
        setManageGoalsToken((value) => value + 1);
      })
      .catch(() => {
        /* resolves rather than rejects */
      });
  };

  /* ── Review (hr.view_reviews) ──────────────────────────────────────────── */

  const [teamEmployeeId, setTeamEmployeeId] = useState<string | null>(null);
  const [teamCycleFilter, setTeamCycleFilter] = useState('');
  const [teamReviewsToken, setTeamReviewsToken] = useState(0);
  const teamReviewsPath =
    teamEmployeeId === null
      ? null
      : hrPerformanceReviewsPath({
          employeeId: teamEmployeeId,
          cycleId: teamCycleFilter.trim() === '' ? undefined : teamCycleFilter.trim(),
        });
  const teamReviews = useApiQuery<ReviewListResponse>(teamReviewsPath, teamReviewsToken);
  const submitManagerReview = useApiMutation<ReviewSubmitRequest, Review>(
    HR_PERFORMANCE_REVIEWS_PATH,
  );

  const loadTeamEmployee = (id: string): void => {
    submitManagerReview.reset();
    setTeamEmployeeId(id);
  };

  const onSubmitManagerReview = (body: ReviewSubmitRequest): void => {
    submitManagerReview
      .submit(body)
      .then(() => {
        setTeamReviewsToken((value) => value + 1);
      })
      .catch(() => {
        /* resolves rather than rejects */
      });
  };

  return (
    <div className="wp-stack wp-f">
      <ScreenHeading>{t('nav.hrPerformance')}</ScreenHeading>
      <div className="wp-f-lede">
        <p className="wp-small wp-muted">{t('hr.performance.intro')}</p>
        <p className="wp-small wp-muted" data-testid="w8-scope-note">
          {t('hr.performance.scopeNote')}
        </p>
      </div>

      <MyReviewPanel
        reviewsStatus={myReviews.status}
        reviews={myReviews.data?.data ?? null}
        reviewsError={myReviews.error}
        onReloadReviews={myReviews.reload}
        goalsStatus={myGoals.status}
        goals={myGoals.data?.data ?? null}
        goalsError={myGoals.error}
        onReloadGoals={myGoals.reload}
        submitPending={submitSelfReview.status === 'pending'}
        submitError={submitSelfReview.error}
        submitted={submitSelfReview.status === 'ok' ? submitSelfReview.data : null}
        onSubmitSelfReview={onSubmitSelfReview}
      />

      {mayManage ? (
        <PerformanceManagePanel
          cyclesStatus={cycles.status}
          cycles={cycles.data?.data ?? null}
          cyclesError={cycles.error}
          onReloadCycles={cycles.reload}
          cycleCreatePending={createCycle.status === 'pending'}
          cycleCreateError={createCycle.error}
          cycleCreated={createCycle.status === 'ok' ? createCycle.data : null}
          onCreateCycle={onCreateCycle}
          employeeId={manageGoalsEmployeeId}
          onLoadEmployee={loadManageGoalsEmployee}
          goalsStatus={manageGoals.status}
          goals={manageGoals.data?.data ?? null}
          goalsError={manageGoals.error}
          onReloadGoals={manageGoals.reload}
          goalCreatePending={createGoal.status === 'pending'}
          goalCreateError={createGoal.error}
          goalCreated={createGoal.status === 'ok' ? createGoal.data : null}
          onCreateGoal={onCreateGoal}
        />
      ) : null}

      {mayReview ? (
        <TeamReviewsPanel
          employeeId={teamEmployeeId}
          onLoadEmployee={loadTeamEmployee}
          cycleFilter={teamCycleFilter}
          onCycleFilterChange={setTeamCycleFilter}
          listStatus={teamReviews.status}
          reviews={teamReviews.data?.data ?? null}
          listError={teamReviews.error}
          onReload={teamReviews.reload}
          submitPending={submitManagerReview.status === 'pending'}
          submitError={submitManagerReview.error}
          submitted={submitManagerReview.status === 'ok' ? submitManagerReview.data : null}
          onSubmitManagerReview={onSubmitManagerReview}
        />
      ) : null}
    </div>
  );
}
